import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { isUnreadKind, type UnreadKind } from '@/config/leadTypes';
import { cn } from '@/lib/ui/cn';

/**
 * B-R16-07: which leads nobody on the team has opened yet.
 *
 * "Opened" is team-wide (owner, 2026-11-02): the first staff member to open an
 * item clears it for everyone. Contact enquiries and seminar applications are
 * opened when their detail dialog is shown; newsletter, interest and Calendly
 * have no detail view, so their rows are opened when a list page shows them.
 */

/** The counts the caller may read; a list it cannot open is absent, not 0. */
export type UnreadCounts = Partial<Record<UnreadKind, number>>;

/** The API caps one batch at 100 ids. */
const BATCH = 100;

export const openedApi = {
  unreadCounts: () => api.get<UnreadCounts>('/admin/leads/unread-counts'),
  markOpened: (kind: UnreadKind, id: string) =>
    api.post<void>(`/admin/leads/${kind}/${encodeURIComponent(id)}/opened`),
  async markRowsOpened(kind: UnreadKind, ids: string[]) {
    for (let i = 0; i < ids.length; i += BATCH) {
      await api.post<void>(`/admin/leads/${kind}/opened`, { ids: ids.slice(i, i + BATCH) });
    }
  },
};

/**
 * The sidebar's counts. Refetched every minute while the tab is visible (not in
 * the background) and on returning to it, so a new enquiry shows up without a
 * reload and a colleague's opening clears it.
 */
export function useUnreadCounts(enabled: boolean) {
  return useQuery({
    queryKey: keys.leadsUnread(),
    queryFn: openedApi.unreadCounts,
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Mark one item opened when its detail is shown. Idempotent on the server
 * (first opener wins), sent at most once per mount, and silent on failure: a
 * missed mark only leaves the badge one too high until someone opens it again.
 */
export function useMarkOpenedOnShow(kind: string, id: string | undefined, openedAt: string | null | undefined, active: boolean) {
  const qc = useQueryClient();
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !id || openedAt || !isUnreadKind(kind) || sent.current === id) return;
    sent.current = id;
    openedApi.markOpened(kind, id).then(
      () => {
        void qc.invalidateQueries({ queryKey: keys.leadsUnread() });
        void qc.invalidateQueries({ queryKey: keys.leads.lists(kind) });
      },
      () => {
        sent.current = null;
      },
    );
  }, [active, id, openedAt, kind, qc]);
}

/**
 * Mark the unopened rows a list page shows as opened. Only the counts are
 * refreshed afterwards: the rows keep their unopened mark for this visit, so
 * staff can still see which ones the count meant.
 */
export function useMarkRowsOpened(kind: UnreadKind, rows: { id: string; opened_at: string | null }[] | undefined, enabled: boolean) {
  const qc = useQueryClient();
  const sent = useRef(new Set<string>());
  const pending = (rows ?? [])
    .filter((r) => !r.opened_at)
    .map((r) => r.id)
    .join(',');
  useEffect(() => {
    if (!enabled || !pending) return;
    const fresh = pending.split(',').filter((id) => !sent.current.has(id));
    if (!fresh.length) return;
    fresh.forEach((id) => sent.current.add(id));
    openedApi.markRowsOpened(kind, fresh).then(
      () => void qc.invalidateQueries({ queryKey: keys.leadsUnread() }),
      () => fresh.forEach((id) => sent.current.delete(id)),
    );
  }, [enabled, pending, kind, qc]);
}

/** The sidebar badge. Hidden at 0; "99+" past 99. Its words are spoken by the link instead. */
export function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      data-testid="unread-badge"
      className="ms-auto min-w-[22px] shrink-0 rounded-pill bg-accent-blue px-[7px] py-[1px] text-center font-mono text-label-sm font-semibold leading-[18px] tabular-nums text-canvas-paper"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** The mark on an unopened row: a dot for the eye, a word for a screen reader. */
export function UnopenedDot({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <>
      <span aria-hidden="true" className={cn('inline-block size-[7px] shrink-0 rounded-full bg-accent-blue', className)} />
      <span className="sr-only">{t('leads.unopened')}</span>
    </>
  );
}
